import { useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  PinInput,
  Stack,
  Typography,
  StepIndicator,
  Divider,
  Pill,
} from "@financeable-com-au/financeable-ui";
import { AuthCard } from "@components/AuthCard";
import {
  AUTH_REQUEST_KEYS,
  authV2MfaVerify,
  authV2Selector,
  clearPendingMfa,
  toAuthV2Error,
} from "@/store/slices/authV2Slice";
import type { AppDispatch } from "@/store/store";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { buildPath } from "@/utils/buildPath";
import { completeAuthNavigation } from "@/utils/standaloneConnect";

const AuthMfa = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const authV2 = useSelector(authV2Selector);
  const isLoading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.mfaVerify]);

  const mfaFromState = location.state?.mfa;
  const fromMagicCode = !!location.state?.magicCode;
  const magicCode = location.state?.magicCode;
  const email = location.state?.email;
  const invitationToken = searchParams.get("invitationToken") ?? searchParams.get("invitation_token") ?? "";
  const pendingMfa = useMemo(() => {
    if (
      mfaFromState &&
      typeof mfaFromState.pendingAuthenticationToken === "string" &&
      typeof mfaFromState.authenticationChallengeId === "string"
    ) {
      return mfaFromState;
    }

    if (
      authV2?.pendingMfa &&
      typeof authV2.pendingMfa.pendingAuthenticationToken === "string" &&
      typeof authV2.pendingMfa.authenticationChallengeId === "string"
    ) {
      return authV2.pendingMfa;
    }

    const pendingAuthenticationToken = searchParams.get("pendingAuthenticationToken") ?? "";
    const authenticationChallengeId = searchParams.get("authenticationChallengeId") ?? "";
    if (!pendingAuthenticationToken || !authenticationChallengeId) {
      return null;
    }

    return {
      pendingAuthenticationToken,
      authenticationChallengeId,
    };
  }, [mfaFromState, authV2?.pendingMfa, searchParams]);

  const verifyMfaCode = async (nextCode: string[] | string) => {
    if (!pendingMfa) {
      formik.setFieldError("code", "Missing MFA challenge details. Please sign in again.");
      return;
    }

    const codeString = Array.isArray(nextCode) ? nextCode.join("") : nextCode;
    const result = await dispatch(
      authV2MfaVerify({
        code: codeString.trim(),
        pendingAuthenticationToken: pendingMfa.pendingAuthenticationToken,
        authenticationChallengeId: pendingMfa.authenticationChallengeId,
      }),
    );

    if (result.error) {
      const parsedError = toAuthV2Error(result.payload);

      // Multiple organisations detected for the auth user.
      if (parsedError.code === "ORGANISATION_SELECTION_REQUIRED" && (parsedError.organisations ?? []).length > 1) {
        navigate(buildPath("/auth/select-organisation", invitationToken ? { invitationToken } : undefined), {
          state: {
            organisation: parsedError.organisations,
            pendingAuthenticationToken: parsedError.pendingAuthenticationToken ?? "",
          },
        });
        return;
      }

      dispatch(
        openSnackbar({
          message: parsedError.message,
          intent: "danger",
        }),
      );
      return;
    }

    dispatch(clearPendingMfa());
    if (invitationToken) {
      navigate(buildPath("/auth/invite/accept", { invitationToken }));
      return;
    }

    completeAuthNavigation(result.payload);
  };

  const formik = useFormik({
    initialValues: {
      code: Array(6).fill("") as string[],
    },
    onSubmit: (values) => {
      verifyMfaCode(values.code);
    },
    validationSchema: yup.object({
      code: yup
        .array()
        .of(yup.string())
        .min(6)
        .test(
          "all-filled",
          "Please enter the full 6-digit code.",
          (value) =>
            Array.isArray(value) && value.length === 6 && value.every((v) => typeof v === "string" && v.trim() !== ""),
        ),
    }),
    validateOnChange: false,
    validateOnBlur: false,
  });

  const navigateToSignIn = () => {
    navigate(buildPath("/auth/sign-in"));
  };

  return (
    <>
      <Stack textAlign="center" rowGap={1}>
        <Typography level="title-lg">Multi-factor authentication</Typography>
      </Stack>
      {/* Action sheet */}
      <AuthCard onSubmit={() => formik.handleSubmit()}>
        <Stack rowGap={3}>
          {!pendingMfa ? (
            <Alert intent="neutral" variant="filled" description="Your MFA challenge has expired or is missing" />
          ) : (
            <>
              <Stack mb={4} rowGap={6}>
                {/* Visual guidance for users from AuthMagic */}
                {fromMagicCode && (
                  <Stack rowGap={6}>
                    <Stack rowGap={4}>
                      <Stack flexDirection="row" gap={2} alignItems="flex-start">
                        <StepIndicator color="primary" size="sm" variant="solid">
                          1
                        </StepIndicator>
                        <Stack flexGrow={2}>
                          <Typography level="body-lg" bold lineHeight={1}>
                            Email code
                          </Typography>
                          {!!email && <Typography level="body-lg">Sent to {email}</Typography>}
                        </Stack>
                        <Pill accent={300}>Verified</Pill>
                      </Stack>
                      <FormControl>
                        <PinInput disabled length={6} size="lg" value={magicCode} gap={2.5} />
                      </FormControl>
                    </Stack>
                    <Divider />
                  </Stack>
                )}
                <Stack rowGap={4} mb={1}>
                  <Stack flexDirection="row" gap={2} alignItems="flex-start">
                    {fromMagicCode && (
                      <StepIndicator color="primary" size="sm" variant="solid">
                        2
                      </StepIndicator>
                    )}
                    <Stack>
                      <Typography level="body-lg" bold lineHeight={1}>
                        Authenticator app code
                      </Typography>
                      <Typography level="body-lg">Enter the code from your authenticator app</Typography>
                    </Stack>
                  </Stack>
                  <FormControl error={!!formik.errors.code}>
                    <PinInput
                      intent={formik.errors.code ? "danger" : undefined}
                      length={6}
                      onChange={(value) => {
                        formik.setFieldValue("code", value);
                        if (formik.errors.code) {
                          formik.setFieldError("code", undefined);
                        }
                      }}
                      onComplete={(value) => {
                        formik.setFieldValue("code", value.split(""));
                        if (formik.errors.code) {
                          formik.setFieldError("code", undefined);
                        }
                        verifyMfaCode(value);
                      }}
                      autoFocus
                      size="lg"
                      value={formik.values.code}
                      gap={2.5}
                    />
                    {formik.errors.code ? (
                      <FormHelperText>
                        <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                        {formik.errors.code}
                      </FormHelperText>
                    ) : null}
                  </FormControl>
                </Stack>
                <Button
                  size="lg"
                  variant="primary"
                  loading={isLoading}
                  endDecorator={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
                  type="submit"
                >
                  Verify
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </AuthCard>
      <Button
        startDecorator={<ChevronLeftIcon sx={{ fontSize: "1.2rem" }} />}
        variant="inline"
        intent="neutral"
        size="lg"
        onClick={navigateToSignIn}
      >
        Go back
      </Button>
    </>
  );
};

export { AuthMfa };
